/**
 * SSR-safe read/write of the `tc_consent` cookie. The cookie stores a small
 * JSON record:  { v: <version>, t: <ISO timestamp>, c: { <category>: bool } },
 * plus `d: 1` when the record holds notice-region defaults rather than a
 * choice the visitor made.
 *
 * On the server (no `document`) `readConsent()` returns null so nothing is
 * granted during SSR — trackers are client-only anyway, and this keeps the
 * first client render matching the server (banner mounts post-hydration).
 */
import Cookies from 'js-cookie';
import {
  CONSENT_COOKIE,
  CONSENT_VERSION,
  CONSENT_MAX_AGE_DAYS,
  NON_NECESSARY,
  REGION_COOKIE,
  REGION_NOTICE,
  REGION_OPT_IN,
  buildConsent,
} from './consentConfig';

export function readConsent() {
  if (typeof document === 'undefined') return null;
  const raw = Cookies.get(CONSENT_COOKIE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    // A version bump (new categories / new policy) invalidates the old choice.
    if (!parsed || parsed.v !== CONSENT_VERSION) return null;
    const categories = buildConsent(false);
    for (const key of NON_NECESSARY) categories[key] = !!parsed.c?.[key];
    return {
      categories,
      version: parsed.v,
      timestamp: parsed.t,
      isDefault: parsed.d === 1,
    };
  } catch {
    return null;
  }
}

/**
 * True when the cookie holds a choice the visitor made under an older
 * CONSENT_VERSION. A version bump must re-ask them, never replace their
 * answer with notice-region defaults.
 */
export function hasStaleChoice() {
  if (typeof document === 'undefined') return false;
  const raw = Cookies.get(CONSENT_COOKIE);
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw);
    return !!parsed && parsed.v !== CONSENT_VERSION && parsed.d !== 1;
  } catch {
    return false;
  }
}

export function writeConsent(categories, { isDefault = false } = {}) {
  const record = { v: CONSENT_VERSION, t: new Date().toISOString(), c: {} };
  if (isDefault) record.d = 1;
  for (const key of NON_NECESSARY) record.c[key] = !!categories?.[key];
  if (typeof document !== 'undefined') {
    Cookies.set(CONSENT_COOKIE, JSON.stringify(record), {
      expires: CONSENT_MAX_AGE_DAYS,
      sameSite: 'Lax',
      secure:
        typeof window !== 'undefined' &&
        window.location?.protocol === 'https:',
      path: '/',
    });
  }
  return record;
}

export function clearConsent() {
  if (typeof document !== 'undefined') {
    Cookies.remove(CONSENT_COOKIE, { path: '/' });
  }
}

/** REGION_NOTICE when middleware placed the visitor outside the opt-in list. */
export function readRegion() {
  if (typeof document === 'undefined') return REGION_OPT_IN;
  return Cookies.get(REGION_COOKIE) === REGION_NOTICE
    ? REGION_NOTICE
    : REGION_OPT_IN;
}

/** True when the browser sends a Global Privacy Control opt-out signal. */
export function readGpc() {
  return typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true;
}
